/**
 * Fasting rule resolution.
 *
 * Inputs: a Gregorian date and the user's discipline level (beginner /
 * standard / strict). Output: the foods permitted that day, plus a label
 * naming the rule.
 *
 * Resolution order (highest precedence first):
 *
 *   1.  Bright Week (Pascha → Pascha+6)            → fast-free
 *   2.  Pentecost / Trinity Week (Pascha+50..+56)  → fast-free
 *   3.  Twelve Days of Christmas (Dec 25 → Jan 4)  → fast-free
 *   4.  Publican-and-Pharisee week                 → fast-free
 *   5.  Annunciation (Mar 25)                      → fish/oil/wine even in Lent (except Holy Fri/Sat)
 *   6.  Palm Sunday                                → fish/oil/wine
 *   7.  Transfiguration (Aug 6)                    → fish/oil/wine even in Dormition Fast
 *   8.  Cheesefare Week (Pascha-55 → Pascha-49)    → no meat, dairy/eggs OK even Wed/Fri
 *   9.  Holy Week (Pascha-7 → Pascha-1)            → strict for all levels
 *  10.  Great Lent
 *  11.  Apostles' Fast
 *  12.  Dormition Fast
 *  13.  Nativity Fast
 *  14.  Default - Wed/Fri are weekly fasts; other days are fast-free.
 */
import { paschaGregorian } from './pascha.js';
import { addDays, dayOfWeek, diffDays, fixedDateFor, isBefore, isOnOrAfter, julianGregorianOffset, toIso, } from './dates.js';
const FREE = {
    allowsMeat: true,
    allowsDairy: true,
    allowsEggs: true,
    allowsFish: true,
    allowsWine: true,
    allowsOil: true,
    isFastFree: true,
};
const XEROPHAGY = {
    allowsMeat: false,
    allowsDairy: false,
    allowsEggs: false,
    allowsFish: false,
    allowsWine: false,
    allowsOil: false,
    isFastFree: false,
};
const FISH_OIL_WINE = {
    allowsMeat: false,
    allowsDairy: false,
    allowsEggs: false,
    allowsFish: true,
    allowsWine: true,
    allowsOil: true,
    isFastFree: false,
};
const OIL_WINE_NO_FISH = {
    allowsMeat: false,
    allowsDairy: false,
    allowsEggs: false,
    allowsFish: false,
    allowsWine: true,
    allowsOil: true,
    isFastFree: false,
};
const NO_MEAT_OK_DAIRY = {
    allowsMeat: false,
    allowsDairy: true,
    allowsEggs: true,
    allowsFish: true,
    allowsWine: true,
    allowsOil: true,
    isFastFree: false,
};
function rule(pattern, label, level, base) {
    return { rulePattern: pattern, label, level, ...base };
}
/* -----------------------------------------------------------------------
 * Period checks - each returns true when `date` falls within the period.
 * --------------------------------------------------------------------- */
function isBrightWeek(date, pascha) {
    const offset = diffDays(pascha, date);
    return offset >= 0 && offset <= 6;
}
function isPentecostWeek(date, pascha) {
    const offset = diffDays(pascha, date);
    return offset >= 50 && offset <= 56;
}
function isTwelveDaysOfChristmas(date) {
    if (date.month === 12 && date.day >= 25)
        return true;
    if (date.month === 1 && date.day >= 1 && date.day <= 4)
        return true;
    return false;
}
function isPublicanPhariseeWeek(date, pascha) {
    const offset = diffDays(pascha, date);
    // Pascha-69 (Mon) through Pascha-63 (Sat), week between Pub/Phar Sun and Prodigal Son Sun.
    return offset >= -69 && offset <= -63;
}
function isCheesefareWeek(date, pascha) {
    const offset = diffDays(pascha, date);
    // Pascha-55 (Mon after Meatfare Sun) through Pascha-49 (Cheesefare/Forgiveness Sun).
    return offset >= -55 && offset <= -49;
}
function isHolyWeek(date, pascha) {
    const offset = diffDays(pascha, date);
    return offset >= -7 && offset <= -1;
}
function isGreatLent(date, pascha) {
    const offset = diffDays(pascha, date);
    // Clean Monday (Pascha-48) through Friday before Lazarus Saturday (Pascha-9).
    return offset >= -48 && offset <= -9;
}
function isApostlesFast(date, pascha, fixed) {
    const offset = diffDays(pascha, date);
    if (offset < 57)
        return false;
    // Begins Pascha+57 (Mon after All Saints), movable, so keyed to the civil
    // date. Ends Jun 28 of the menaion (eve of Sts Peter and Paul), fixed, so
    // keyed to the shifted date for Old Calendar (civil Jul 11).
    if (fixed.month < 6)
        return false;
    if (fixed.month === 6 && fixed.day > 28)
        return false;
    if (fixed.month > 6)
        return false;
    return true;
}
function isDormitionFast(date) {
    return date.month === 8 && date.day >= 1 && date.day <= 14;
}
function isNativityFast(date) {
    if (date.month === 11 && date.day >= 15)
        return true;
    if (date.month === 12 && date.day <= 24)
        return true;
    return false;
}
/* -----------------------------------------------------------------------
 * Per-period rule resolvers.
 * --------------------------------------------------------------------- */
function holyWeekRule(date, pascha, level) {
    const offset = diffDays(pascha, date);
    if (offset === -2)
        return rule('great_friday', 'Great Friday', level, XEROPHAGY);
    if (offset === -7)
        return rule('lent_annunciation_or_palm', 'Palm Sunday', level, FISH_OIL_WINE);
    return rule('holy_week', 'Holy Week', level, XEROPHAGY);
}
function greatLentRule(date, pascha, level) {
    const dow = dayOfWeek(date);
    const offset = diffDays(pascha, date);
    // First week of Lent, the strictest. Days Pascha-48..Pascha-42.
    if (offset >= -48 && offset <= -42) {
        return rule('lent_first_week', 'First Week of Great Lent', level, XEROPHAGY);
    }
    // Saturdays and Sundays of Lent, wine and oil permitted at all levels.
    if (dow === 0 || dow === 6) {
        if (level === 'beginner') {
            return rule('lent_saturday_sunday', 'Lenten Saturday/Sunday', level, FISH_OIL_WINE);
        }
        return rule('lent_saturday_sunday', 'Lenten Saturday/Sunday', level, OIL_WINE_NO_FISH);
    }
    // Lenten weekdays.
    if (level === 'beginner') {
        return rule('lent_weekday', 'Lenten weekday', level, OIL_WINE_NO_FISH);
    }
    return rule('lent_weekday', 'Lenten weekday', level, XEROPHAGY);
}
function apostlesFastRule(date, level) {
    const dow = dayOfWeek(date);
    if (dow === 0 || dow === 6) {
        return rule('apostles_sat_sun', 'Apostles’ Fast, Saturday/Sunday', level, FISH_OIL_WINE);
    }
    if (dow === 3 || dow === 5) {
        if (level === 'beginner') {
            return rule('apostles_wed_fri', 'Apostles’ Fast, Wed/Fri', level, OIL_WINE_NO_FISH);
        }
        return rule('apostles_wed_fri', 'Apostles’ Fast, Wed/Fri', level, XEROPHAGY);
    }
    if (level === 'strict') {
        return rule('apostles_mon_thu', 'Apostles’ Fast, weekday', level, OIL_WINE_NO_FISH);
    }
    return rule('apostles_mon_thu', 'Apostles’ Fast, weekday', level, FISH_OIL_WINE);
}
function dormitionFastRule(date, level) {
    const dow = dayOfWeek(date);
    if (dow === 0 || dow === 6) {
        return rule('dormition_sat_sun', 'Dormition Fast, Saturday/Sunday', level, OIL_WINE_NO_FISH);
    }
    if (level === 'beginner') {
        return rule('dormition_weekday', 'Dormition Fast, weekday', level, OIL_WINE_NO_FISH);
    }
    return rule('dormition_weekday', 'Dormition Fast, weekday', level, XEROPHAGY);
}
function nativityFastRule(date, fixed, level) {
    const dow = dayOfWeek(date);
    // Stricter from Dec 20 to Dec 24 inclusive, a fixed-cycle phase, so keyed
    // to the menaion (shifted) date; the weekday still comes from the civil one.
    const inFinalWeek = fixed.month === 12 && fixed.day >= 20 && fixed.day <= 24;
    if (dow === 0 || dow === 6) {
        return rule('nativity_phase1_sat_sun', 'Nativity Fast, Saturday/Sunday', level, FISH_OIL_WINE);
    }
    if (dow === 3 || dow === 5) {
        if (level === 'beginner') {
            return rule('nativity_phase1_wed_fri', 'Nativity Fast, Wed/Fri', level, OIL_WINE_NO_FISH);
        }
        return rule('nativity_phase1_wed_fri', 'Nativity Fast, Wed/Fri', level, XEROPHAGY);
    }
    if (inFinalWeek) {
        if (level === 'beginner') {
            return rule('nativity_phase2_dec_20_24', 'Nativity Fast - Dec 20-24', level, OIL_WINE_NO_FISH);
        }
        return rule('nativity_phase2_dec_20_24', 'Nativity Fast, Dec 20-24', level, XEROPHAGY);
    }
    if (level === 'strict') {
        return rule('nativity_phase1_mon_thu', 'Nativity Fast, weekday', level, OIL_WINE_NO_FISH);
    }
    return rule('nativity_phase1_mon_thu', 'Nativity Fast, weekday', level, FISH_OIL_WINE);
}
function weeklyDefault(date, level) {
    const dow = dayOfWeek(date);
    if (dow === 3 || dow === 5) {
        if (level === 'beginner') {
            return rule(dow === 3 ? 'wednesday' : 'friday', dow === 3 ? 'Wednesday fast' : 'Friday fast', level, FISH_OIL_WINE);
        }
        if (level === 'standard') {
            return rule(dow === 3 ? 'wednesday' : 'friday', dow === 3 ? 'Wednesday fast' : 'Friday fast', level, OIL_WINE_NO_FISH);
        }
        return rule(dow === 3 ? 'wednesday' : 'friday', dow === 3 ? 'Wednesday fast' : 'Friday fast', level, XEROPHAGY);
    }
    return rule('default', 'No fast', level, FREE);
}
/* -----------------------------------------------------------------------
 * Public entry point.
 * --------------------------------------------------------------------- */
export function fastingForDate(date, level = 'standard', calendarStyle = 'new') {
    const pascha = paschaGregorian(date.year);
    const offset = diffDays(pascha, date);
    // Fixed-cycle checks (Christmas, Annunciation, Transfiguration, the Dormition
    // and Nativity fasts, the end of the Apostles' fast) use the menaion date,
    // shifted for Old Calendar. Movable checks (Bright/Pentecost/Lent/Holy Week,
    // the Apostles' fast start) and the day-of-week use the civil date.
    const fixed = fixedDateFor(date, calendarStyle);
    // 1. Bright Week (movable)
    if (isBrightWeek(date, pascha))
        return rule('bright_week', 'Bright Week, fast-free', level, FREE);
    // 2. Pentecost (Trinity) Week (movable)
    if (isPentecostWeek(date, pascha))
        return rule('pentecost_week', 'Pentecost Week, fast-free', level, FREE);
    // 3. Twelve Days of Christmas (fixed)
    if (isTwelveDaysOfChristmas(fixed))
        return rule('twelve_days_of_christmas', 'Twelve Days of Christmas, fast-free', level, FREE);
    // 4. Publican-and-Pharisee Week (movable)
    if (isPublicanPhariseeWeek(date, pascha))
        return rule('publican_pharisee_week', 'Week after the Publican and Pharisee, fast-free', level, FREE);
    // 5/6/7. Feast overrides, but Holy Week wins over them.
    const inHolyWeek = isHolyWeek(date, pascha);
    if (!inHolyWeek) {
        if (fixed.month === 3 && fixed.day === 25) {
            return rule('lent_annunciation_or_palm', 'Annunciation', level, FISH_OIL_WINE);
        }
        if (offset === -7) {
            return rule('lent_annunciation_or_palm', 'Palm Sunday', level, FISH_OIL_WINE);
        }
        if (fixed.month === 8 && fixed.day === 6) {
            return rule('transfiguration', 'Transfiguration', level, FISH_OIL_WINE);
        }
    }
    // 8. Cheesefare Week (movable)
    if (isCheesefareWeek(date, pascha)) {
        return rule('cheesefare_week', 'Cheesefare Week', level, NO_MEAT_OK_DAIRY);
    }
    // 9. Holy Week (movable)
    if (inHolyWeek)
        return holyWeekRule(date, pascha, level);
    // 10. Great Lent (movable)
    if (isGreatLent(date, pascha))
        return greatLentRule(date, pascha, level);
    // 11. Apostles' Fast (movable start, fixed end)
    if (isApostlesFast(date, pascha, fixed))
        return apostlesFastRule(date, level);
    // 12. Dormition Fast (fixed)
    if (isDormitionFast(fixed))
        return dormitionFastRule(date, level);
    // 13. Nativity Fast (fixed)
    if (isNativityFast(fixed))
        return nativityFastRule(date, fixed, level);
    // 14. Default - Wed/Fri are weekly fasts; everything else is fast-free.
    return weeklyDefault(date, level);
}
/** Helper: compute all three levels in one call. */
export function fastingForDateAllLevels(date, calendarStyle = 'new') {
    return {
        beginner: fastingForDate(date, 'beginner', calendarStyle),
        standard: fastingForDate(date, 'standard', calendarStyle),
        strict: fastingForDate(date, 'strict', calendarStyle),
    };
}
function inRange(date, start, end) {
    return isOnOrAfter(date, start) && !isBefore(end, date) && isOnOrAfter(end, date);
}
function makePeriod(slug, name, kind, start, end) {
    return { slug, name, kind, start, end, startIso: toIso(start), endIso: toIso(end) };
}
/**
 * Returns every named period that touches `year`. A period that crosses
 * the year boundary (e.g. Twelve Days of Christmas, Nativity Fast) is
 * emitted for each anchor year whose span intersects `year` so the
 * calendar can render it.
 *
 * `calendarStyle`: movable (Pascha-anchored) periods are shared by both
 * calendars and never shift. Fixed-cycle periods are expressed in menaion
 * dates and mapped to the civil calendar, for Old Calendar that is
 * +13 days (Dormition Fast civil Aug 14–27, Nativity Fast Nov 28 → Jan 6,
 * Twelve Days Jan 7–17). The Apostles' Fast keeps its movable start and
 * shifts only its fixed end (eve of Sts Peter & Paul).
 */
export function fastingPeriodsForYear(year, calendarStyle = 'new') {
    const periods = [];
    const push = (slug, name, kind, start, end) => {
        if (start.year > year || end.year < year)
            return; // does not touch this civil year
        periods.push(makePeriod(slug, name, kind, start, end));
    };
    // Anchor on both the previous and current year so spans that cross the
    // civil-year boundary (Nativity Fast, Twelve Days, a late Pentecost week)
    // are still reported inside `year`.
    for (const y of [year - 1, year]) {
        const pascha = paschaGregorian(y);
        // Movable, Pascha-anchored - shared by both calendars.
        push('publican-pharisee-week', 'Week of the Publican and Pharisee', 'fast-free', addDays(pascha, -69), addDays(pascha, -63));
        push('cheesefare-week', 'Cheesefare Week', 'preparatory', addDays(pascha, -55), addDays(pascha, -49));
        push('great-lent', 'Great Lent', 'fast', addDays(pascha, -48), addDays(pascha, -9));
        push('holy-week', 'Holy Week', 'fast', addDays(pascha, -7), addDays(pascha, -1));
        push('bright-week', 'Bright Week', 'fast-free', pascha, addDays(pascha, 6));
        push('pentecost-week', 'Pentecost (Trinity) Week', 'fast-free', addDays(pascha, 50), addDays(pascha, 56));
        // Fixed-cycle periods: menaion date → civil date for this style.
        const off = calendarStyle === 'old' ? julianGregorianOffset(y) : 0;
        const civil = (month, day) => addDays({ year: y, month, day }, off);
        // Apostles' Fast - movable start, fixed end (menaion Jun 28).
        const apostlesStart = addDays(pascha, 57);
        const apostlesEnd = civil(6, 28);
        if (isOnOrAfter(apostlesEnd, apostlesStart)) {
            push('apostles-fast', "Apostles' Fast", 'fast', apostlesStart, apostlesEnd);
        }
        push('dormition-fast', 'Dormition Fast', 'fast', civil(8, 1), civil(8, 14));
        push('nativity-fast', 'Nativity Fast', 'fast', civil(11, 15), civil(12, 24));
        // Twelve Days of Christmas: menaion Dec 25 → Jan 4 (11 days inclusive).
        push('twelve-days-of-christmas', 'Twelve Days of Christmas', 'fast-free', civil(12, 25), addDays(civil(12, 25), 10));
    }
    return periods;
}
/**
 * Returns the most-specific named period containing `date`, or `null` if
 * the date falls in ordinary time (where only weekly Wed/Fri fasts apply).
 * "Most specific" follows the same precedence as `fastingForDate`.
 */
export function fastingPeriodForDate(date, calendarStyle = 'new') {
    const periods = fastingPeriodsForYear(date.year, calendarStyle);
    // Precedence: shorter / more-specific periods first.
    const PRECEDENCE = {
        'holy-week': 0,
        'bright-week': 1,
        'pentecost-week': 2,
        'twelve-days-of-christmas': 3,
        'publican-pharisee-week': 4,
        'cheesefare-week': 5,
        'great-lent': 6,
        'apostles-fast': 7,
        'dormition-fast': 8,
        'nativity-fast': 9,
    };
    const matches = periods.filter((p) => inRange(date, p.start, p.end));
    if (matches.length === 0)
        return null;
    matches.sort((a, b) => (PRECEDENCE[a.slug] ?? 99) - (PRECEDENCE[b.slug] ?? 99));
    return matches[0];
}
// Re-export helpers for tests.
export { isBefore, isOnOrAfter };
//# sourceMappingURL=fasting.js.map